import { AttributeType, AttributeValue } from '@/components/features/Search/Facets/useFacets';
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Button,
  Flex,
  Tag,
  TagCloseButton,
  chakra,
} from '@chakra-ui/react';
import { capital } from 'case';
import useTranslation from 'next-translate/useTranslation';
import { FC, useState } from 'react';
import AttributeFacetSelection from './AttributeFacetSelection';
import AttributeFacetSlider from './AttributeFacetSlider';

type AttributeAccordionProps = {
  attribute: string;
  showCount?: boolean;
  type: AttributeType;
  facetValues: AttributeValue[];
  addFilter: (facetValue: string) => void;
  addRangeFilter: (min: number, max: number) => void;
  removeFilter: (facetValue?: string) => void;
};

const AttributeAccordion: FC<AttributeAccordionProps> = ({
  attribute,
  type,
  facetValues,
  addFilter,
  addRangeFilter,
  removeFilter,
  showCount,
}) => {
  const { t } = useTranslation('search');

  const selectedValues = facetValues.filter(value => value.isSelected).map(value => value.value);

  const [, setOpen] = useState(!!selectedValues);

  const unselectedValues = facetValues.filter(value => !value.isSelected);

  /** Don't render the filter if there are no items */
  if (!facetValues.length && !selectedValues.length) return null;

  return (
    <Accordion
      allowMultiple
      w="full"
      defaultIndex={type === AttributeType.RANGE && !!selectedValues.length ? [0] : undefined}
    >
      <AccordionItem border={0} bg="transparent">
        <chakra.button
          background="gray.2"
          padding={0}
          rounded="md"
          position="relative"
          w="full"
          color="gray.1"
          borderWidth="1px"
          borderColor="border"
          _hover={{
            color: 'white',
          }}
        >
          <AccordionButton
            as="span"
            border="1px solid transparent"
            fontSize="sm"
            _hover={{
              background: 'none',
              borderColor: 'transparent',
            }}
            display="flex"
            justifyContent="space-between"
            py="sm"
            color={!!selectedValues.length ? 'white' : 'inherit'}
            _expanded={{
              color: 'white',
              borderColor: 'transparent',
            }}
            onClick={() => setOpen(current => !current)}
          >
            {capital(attribute)}
            {(type === AttributeType.RANGE || !!unselectedValues.length) && <AccordionIcon />}
          </AccordionButton>
          {type === AttributeType.SELECTION && !!selectedValues.length && (
            <>
              <Flex flexWrap="wrap" mx="space.16" textAlign="left" gap="space.4">
                {selectedValues.map(value => (
                  <Tag
                    key={`selected-item-${value}`}
                    bg="primary"
                    size="sm"
                    px="space.8"
                    color="darkPurple"
                    mr="space.4"
                    mb="space.4"
                    py="space.6"
                    rounded="radius.4"
                    onClick={() => {
                      removeFilter(value);
                    }}
                    transition="color 120ms ease-in-out"
                    _hover={{ bg: 'primaryLight' }}
                  >
                    {`${value}`.replace(/_/g, ' ')}
                    <TagCloseButton as="span" color="darkPurple" />
                  </Tag>
                ))}
              </Flex>

              <Button
                as="span"
                role="button"
                variant="link"
                fontSize="xs"
                w="full"
                pb="space.16"
                mx="space.16"
                fontWeight="medium"
                color="white"
                justifyContent="flex-start"
                onClick={() => removeFilter()}
              >
                {t('facets.actions.clear')}
              </Button>
            </>
          )}
        </chakra.button>

        <AccordionPanel
          mb={0}
          p={0}
          position="relative"
          bg="transparent"
          rounded="none"
          // When user select all the attributes, the panel should be hidden
          {...(type === AttributeType.SELECTION && unselectedValues.length <= 0
            ? {
                mt: 0,
                visibility: 'hidden',
              }
            : {
                mt: 'space.2',
                visbility: 'visible',
              })}
          zIndex={1}
        >
          {type === AttributeType.SELECTION && (
            <AttributeFacetSelection
              values={unselectedValues}
              addFilter={addFilter}
              showCount={showCount}
            />
          )}

          {type === AttributeType.RANGE && (
            <AttributeFacetSlider
              facetValues={facetValues}
              selectedValues={selectedValues}
              addRangeFilter={addRangeFilter}
              reset={removeFilter}
            />
          )}
        </AccordionPanel>
      </AccordionItem>
    </Accordion>
  );
};

export default AttributeAccordion;
